/**
 * noteText — the single parser for note bodies on web and mobile.
 *
 * Notes on work orders and services are stored as plain text
 * (`work_order_notes.content`); the only markup the backend knows about is the
 * mention form `@[Display Name](userId)` written by the composer. Everything a
 * reader sees as clickable — mentions, links, phone numbers, work order and
 * invoice references — is decided here, so the two renderers
 * (`LinkedText.web` / `LinkedText.native`) only choose how a segment looks,
 * never what counts as one.
 *
 * Display-ID detection, and why digit width tells the two apart:
 *   - A work order display ID is `{location.work_order_prefix}-{%06d counter}`
 *     (postgres_location_repository.go) — e.g. "WO-001234". The prefix is
 *     per-location and operator-editable, so it can't be hardcoded; the
 *     zero-padded 6-digit counter is what's stable.
 *   - An invoice batch display ID is `{account abbreviation}-{%04d counter}`
 *     (postgres_invoice_batch_repository.go) — e.g. "MT-0042".
 *
 * Work orders are matched on shape alone (5-8 digits), which nothing an
 * automotive shop writes collides with. Invoice batches are NOT: their 3-4
 * digit shape is exactly what a shop types all day — "F-150", "RAM-1500",
 * "CX-5". So invoice detection is opt-in — a caller that knows the account
 * name passes its abbreviation as `invoicePrefixes` (see
 * `abbreviateAccountName`) and only tokens with that prefix linkify. Callers
 * without account context get no invoice links rather than a page of false
 * ones.
 */
import { parsePhoneNumberFromString } from 'libphonenumber-js';
/** Digit widths that separate a work order counter from an invoice batch counter. */
const WORK_ORDER_DIGITS = { min: 5, max: 8 };
// Ordered alternation: the scanner tries these left to right at each position,
// so an earlier alternative wins any overlap. Mentions first (a display name
// can contain anything), then URLs — so a query string holding an "@" or a
// phone-shaped number is consumed whole rather than picked apart.
const MENTION_SOURCE = '@\\[([^\\]]+)\\]\\(([^)]+)\\)';
const URL_SOURCE = '(?:https?:\\/\\/|www\\.)[^\\s<>"\'`]+';
const EMAIL_SOURCE = '[A-Za-z0-9._%+-]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*';
// Prefix charset mirrors the LocationModal input mask ([A-Za-z0-9_-], 10 max).
const REFERENCE_SOURCE = '[A-Za-z][A-Za-z0-9_]{0,9}-\\d{3,8}';
// NANP shapes plus a bare E.164. Anything looser drags in dates and part
// numbers; candidates are confirmed by libphonenumber-js below.
const PHONE_SOURCE = '(?:\\+?\\d{1,3}[\\s.-]?)?(?:\\(\\d{3}\\)|\\d{3})[\\s.-]?\\d{3}[\\s.-]?\\d{4}(?:\\s?(?:x|ext\\.?|#)\\s?\\d{1,6})?|\\+\\d{7,15}';
const CANDIDATE_RE = new RegExp(`(${MENTION_SOURCE})|(${URL_SOURCE})|(${EMAIL_SOURCE})|(${REFERENCE_SOURCE})|(${PHONE_SOURCE})`, 'g');
// Capture-group offsets into a CANDIDATE_RE match. MENTION_SOURCE carries two
// inner groups of its own, so the later alternatives are not simply 2, 3, 4…
const GROUP = { mention: 1, mentionName: 2, mentionUserId: 3, url: 4, email: 5, reference: 6 };
/** Trailing characters that read as sentence punctuation rather than part of a URL. */
const TRAILING_PUNCTUATION = new Set(['.', ',', ';', ':', '!', '?', "'", '"', '>']);
/**
 * Trim sentence punctuation off the end of a URL match, keeping a closing paren
 * the URL itself opened ("…/Foo_(bar)" survives whole, while "(see …/foo)" does
 * not swallow the wrapping paren).
 */
function trimUrlTail(match) {
    let end = match.length;
    while (end > 0) {
        const char = match[end - 1];
        if (TRAILING_PUNCTUATION.has(char)) {
            end -= 1;
            continue;
        }
        if (char === ')') {
            const slice = match.slice(0, end);
            const opens = (slice.match(/\(/g) || []).length;
            const closes = (slice.match(/\)/g) || []).length;
            if (closes > opens) {
                end -= 1;
                continue;
            }
        }
        break;
    }
    return match.slice(0, end);
}
const isDigit = (char) => char !== undefined && char >= '0' && char <= '9';
/**
 * Uppercase first letter of each word — the TypeScript twin of
 * abbreviateAccountName in postgres_invoice_batch_repository.go, so the UI
 * derives the same invoice batch prefix the backend generates.
 */
export function abbreviateAccountName(name) {
    const abbreviation = (name || '')
        .split(/\s+/)
        .map((word) => {
        const letter = word.match(/[A-Za-z]/);
        return letter ? letter[0].toUpperCase() : '';
    })
        .join('');
    return abbreviation || 'IB';
}
/** Classify a "PREFIX-DIGITS" token, or null when it isn't a reference we link. */
function classifyReference(match, invoicePrefixes, workOrderPrefixes, detect) {
    const separator = match.lastIndexOf('-');
    if (separator <= 0)
        return null;
    const prefix = match.slice(0, separator).toUpperCase();
    const digits = match.slice(separator + 1);
    if (detect.invoice && invoicePrefixes.some((candidate) => candidate.toUpperCase() === prefix)) {
        return 'invoice';
    }
    if (!detect.workOrder)
        return null;
    if (digits.length < WORK_ORDER_DIGITS.min || digits.length > WORK_ORDER_DIGITS.max)
        return null;
    if (workOrderPrefixes.length > 0 && !workOrderPrefixes.some((candidate) => candidate.toUpperCase() === prefix)) {
        return null;
    }
    return 'workOrder';
}
/**
 * Parse note text into renderable segments.
 *
 * The segments always reconstruct the input exactly (via `raw`), so a renderer
 * can map over them without worrying about dropping characters.
 */
export function parseNoteText(input, options = {}) {
    if (!input)
        return [];
    const detect = {
        url: options.detect?.url !== false,
        email: options.detect?.email !== false,
        phone: options.detect?.phone !== false,
        workOrder: options.detect?.workOrder !== false,
        invoice: options.detect?.invoice !== false,
    };
    const invoicePrefixes = options.invoicePrefixes ?? [];
    const workOrderPrefixes = options.workOrderPrefixes ?? [];
    const defaultCountry = options.defaultCountry ?? 'US';
    const segments = [];
    let pending = '';
    let cursor = 0;
    const flushPending = () => {
        if (pending) {
            segments.push({ type: 'text', text: pending, raw: pending, value: pending });
            pending = '';
        }
    };
    const push = (type, text, raw, value) => {
        flushPending();
        segments.push({ type, text, raw, value });
    };
    CANDIDATE_RE.lastIndex = 0;
    let match;
    while ((match = CANDIDATE_RE.exec(input)) !== null) {
        const whole = match[0];
        const start = match.index;
        // Guard against a zero-length match wedging the loop.
        if (whole.length === 0) {
            CANDIDATE_RE.lastIndex += 1;
            continue;
        }
        pending += input.slice(cursor, start);
        cursor = start + whole.length;
        if (match[GROUP.mention]) {
            push('mention', `@${match[GROUP.mentionName]}`, whole, match[GROUP.mentionUserId]);
            continue;
        }
        const url = match[GROUP.url];
        if (url && detect.url) {
            const trimmed = trimUrlTail(url);
            const href = trimmed.toLowerCase().startsWith('www.') ? `https://${trimmed}` : trimmed;
            push('url', trimmed, trimmed, href);
            // Punctuation trimmed off the tail belongs to the surrounding sentence.
            cursor = start + trimmed.length;
            CANDIDATE_RE.lastIndex = cursor;
            continue;
        }
        const email = match[GROUP.email];
        if (email && detect.email) {
            // The domain alternation can stop on a trailing dot ("email me at a@b.");
            // a TLD-less match is prose, not an address.
            if (/\.[A-Za-z]{2,}$/.test(email)) {
                push('email', email, email, email);
            }
            else {
                pending += whole;
            }
            continue;
        }
        const reference = match[GROUP.reference];
        if (reference) {
            const type = classifyReference(reference, invoicePrefixes, workOrderPrefixes, detect);
            if (type) {
                push(type, reference, reference, reference);
            }
            else {
                pending += whole;
            }
            continue;
        }
        if (detect.phone) {
            // Reject a 10-digit run sliced out of a longer one (a VIN tail, a
            // 16-digit reference number) before paying for full validation.
            const touchesDigit = isDigit(input[start - 1]) || isDigit(input[cursor]);
            const parsed = touchesDigit ? null : parsePhoneNumberFromString(whole, defaultCountry);
            if (parsed && parsed.isValid()) {
                push('phone', whole, whole, parsed.format('E.164'));
                continue;
            }
        }
        pending += whole;
    }
    pending += input.slice(cursor);
    flushPending();
    return segments;
}
/**
 * Clip parsed segments to `maxLength` displayed characters, appending an
 * ellipsis when anything was dropped.
 *
 * Truncating the raw string first (what the mobile notes list used to do) cuts
 * through `@[Name](id)` markup and through URLs, leaving broken markup and
 * half-links on screen. Clipping segments keeps every link whole: a link is
 * kept only if it fits entirely.
 */
export function truncateNoteSegments(segments, maxLength) {
    if (maxLength <= 0)
        return segments;
    const kept = [];
    let budget = maxLength;
    for (const segment of segments) {
        if (segment.text.length <= budget) {
            kept.push(segment);
            budget -= segment.text.length;
            continue;
        }
        // Plain prose can be cut mid-segment; a link is all-or-nothing.
        if (segment.type === 'text' && budget > 0) {
            const clipped = segment.text.slice(0, budget);
            kept.push({ type: 'text', text: clipped, raw: clipped, value: clipped });
        }
        kept.push({ type: 'text', text: '…', raw: '', value: '…' });
        return kept;
    }
    return kept;
}
/** Mention markup rendered as plain text: "@[Jane Doe](id)" becomes "@Jane Doe". */
export function renderMentionsAsText(text) {
    return parseNoteText(text, { detect: { url: false, email: false, phone: false, workOrder: false, invoice: false } })
        .map((segment) => segment.text)
        .join('');
}
/**
 * Whether a note @mentions the given user. Used to emphasize actionable notes
 * that are waiting on the current viewer.
 */
export function noteMentionsUser(text, userId) {
    return !!text && !!userId && text.includes(`](${userId})`);
}
